import React, { useCallback, useMemo, useState } from 'react'

import { CircularProgress, Grid, Typography } from '@mui/material'
import { Button, Link, useGetList, useTranslate } from 'react-admin'

import { Center, Tooltip } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { API } from '../../helpers/Api.helper'
import { DateHelper } from '../../helpers/Date.helper'
import { NumberFormatter } from '../../helpers/NumberFormatter.helper'
import { useUserStore } from '../../stores/useUserStore'
import { Prepayment, PrepaymentTargetType } from '../../types/Prepayments'
import { LXNoData } from '../LXNoData'
import { LXField } from './../LXField'
import { ArtworkTarget } from './ArtworkTarget'
import { CreatePrepayment } from './CreatePrepayment'
import { DeletePrepayment } from './DeletePrepayment'
import { EditPrepayment } from './EditPrepayment'
import { PrepaymentDetails } from './PrepaymentDetails'

export interface ShowPrepaymentsProps {
  targetType: PrepaymentTargetType
  targetId: string
}

export const ShowPrepayments: React.FC<ShowPrepaymentsProps> = ({ targetType, targetId }) => {
  const translate = useTranslate()
  const userStore = useUserStore()

  const [isCreatePrepaymentModalOpen, setIsCreatePrepaymentModalOpen] = useState(false)

  const params = useMemo(() => {
    if (targetType === 'beneficiary') {
      return {
        beneficiaryId: targetId,
      }
    }
    if (targetType === 'product') {
      return {
        productId: targetId,
      }
    }

    return {
      targetType,
      targetId,
    }
  }, [targetType, targetId])

  const prepaymentsQuery = useQuery<Prepayment[]>({
    queryKey: ['prepayments', params],
    queryFn: async () => {
      const response = await API.get<Prepayment[]>('/api/prepayments', {
        params,
      })

      return response.data
    },
  })

  const productsQuery = useGetList(
    'products',
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'ASC' },
      filter: {
        beneficiaryId: params.beneficiaryId,
      },
    },
    {
      enabled: targetType === 'beneficiary',
    },
  )

  const getTarget = useCallback(
    (_targetType: PrepaymentTargetType, _targetId: Prepayment['targetId']) => {
      if (_targetType === 'product') {
        const product = productsQuery.data?.find(a => a.id === _targetId)

        if (product) {
          return <Link to={`/products/${product.id}/show/prepayments`}>{product.name}</Link>
        }

        return _targetId
      }

      if (_targetType === 'artwork') {
        return <ArtworkTarget artworkId={_targetId} />
      }

      return ''
    },
    [productsQuery.data],
  )

  return (
    <>
      <Grid container justifyContent="flex-end">
        <Grid item lg={2}>
          <Tooltip
            label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
              'pages.users.permissionsList.statements.edit',
            )}`}
            hidden={userStore.isAuthorised('statements.edit')}
          >
            <span>
              <Button
                variant="outlined"
                onClick={() => {
                  setIsCreatePrepaymentModalOpen(true)
                }}
                label={translate('pages.prepayments.addPrepayment')}
                disabled={!userStore.isAuthorised('statements.edit')}
              />
            </span>
          </Tooltip>
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={1}>
        {prepaymentsQuery.isLoading ? (
          <Center my="xl" w="100%">
            <CircularProgress size="2rem" />
          </Center>
        ) : (prepaymentsQuery?.data ?? []).length > 0 ? (
          (prepaymentsQuery?.data ?? []).map(
            ({
              id,
              label,
              amountInEuros,
              consumedInEuros,
              remainingInEuros,
              recognitionPeriod,
              ...prepayment
            }) => (
              <Grid container item direction="row" lg={12} spacing={2} key={id}>
                {label && (
                  <LXField label={translate('_generics.name')} size={2} labeled>
                    {label}
                  </LXField>
                )}
                <LXField label={translate('_generics.period')} size={1} labeled>
                  {DateHelper.format(recognitionPeriod, 'MMM YYYY')}
                </LXField>
                <LXField label={translate('pages.prepayments.amountInEuros')} size={2} labeled>
                  {NumberFormatter.formatEuros(amountInEuros)}
                </LXField>
                <LXField label={translate('pages.prepayments.consumedInEuros')} size={2} labeled>
                  {NumberFormatter.formatEuros(consumedInEuros)}
                </LXField>
                <LXField label={translate('pages.prepayments.remainingInEuros')} size={2} labeled>
                  {NumberFormatter.formatEuros(remainingInEuros)}
                </LXField>
                {(targetType === 'beneficiary' && prepayment.targetType !== 'beneficiary') ||
                (targetType === 'product' && prepayment.targetType !== 'product') ? (
                  <LXField label={translate('_generics.appliedOn')} size={2} labeled>
                    <Grid item spacing={0}>
                      <i>{translate(`pages.prepayments.targetType.${prepayment.targetType}`)}</i>
                      <Typography>
                        {getTarget(prepayment.targetType, prepayment.targetId)}
                      </Typography>
                    </Grid>
                  </LXField>
                ) : (
                  <Grid item lg={2} />
                )}
                <Grid container item lg={1} alignItems="center">
                  <PrepaymentDetails prepaymentId={id} prepaymentLabel={label} />
                  {userStore.isAuthorised('statements.edit') && (
                    <>
                      <EditPrepayment
                        prepaymentId={id}
                        prepaymentLabel={label}
                        prepaymentAmountInEuros={amountInEuros}
                        prepaymentRecognitionPeriod={recognitionPeriod}
                        onEdit={() => prepaymentsQuery.refetch()}
                      />
                      <DeletePrepayment
                        prepaymentId={id}
                        prepaymentLabel={label}
                        onDelete={() => prepaymentsQuery.refetch()}
                      />
                    </>
                  )}
                </Grid>
              </Grid>
            ),
          )
        ) : (
          <LXNoData />
        )}
      </Grid>
      <CreatePrepayment
        targetType={targetType}
        targetId={targetId}
        isModalOpen={isCreatePrepaymentModalOpen}
        setIsModalOpen={setIsCreatePrepaymentModalOpen}
        onSuccess={() => prepaymentsQuery.refetch()}
      />
    </>
  )
}
